import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/store/auth';
import { SESSION_TIMEOUT, MIN_UPDATE_INTERVAL } from '@/config/routes';

/**
 * 세션 타임아웃을 관리하는 컴포넌트
 * 사용자의 활동(마우스 이동, 키보드 입력, 클릭 등)을 감지하여 세션 활동 시간을 업데이트
 */
export const SessionTimeoutManager = () => {
  const { isAuthenticated, updateActivity } = useAuthStore();
  const debounceTimerRef = useRef<number | null>(null);
  const lastActivityTimeRef = useRef<number>(Date.now());
  const initialActivityRecordedRef = useRef<boolean>(false);
  const sessionCheckTimerRef = useRef<number | null>(null);
  
  // 디바운싱된 활동 업데이트 함수 - API 호출 최소화
  const debouncedUpdateActivity = () => {
    const now = Date.now();
    const timeSinceLastUpdate = now - lastActivityTimeRef.current;
    
    // 마지막 업데이트 후 최소 10분(600000ms)이 지났을 때만 API 업데이트
    if (timeSinceLastUpdate > MIN_UPDATE_INTERVAL) {
      console.log('[세션] 활동 감지: API 호출 (10분 경과)');
      updateActivity();
      lastActivityTimeRef.current = now;
      return;
    }
    
    // 이미 디바운스 타이머가 있다면 취소
    if (debounceTimerRef.current !== null) {
      window.clearTimeout(debounceTimerRef.current);
    }
    
    // 타이머 설정 없이 내부적으로만 마지막 활동 시간 업데이트
    // 실제 API 호출은 하지 않고 로컬에서만 세션 활성 상태 유지
    lastActivityTimeRef.current = now;
  };

  // 세션 타임아웃 체크 함수 - 주기적으로 호출하여 세션 상태 확인
  const checkSessionTimeout = () => {
    if (!isAuthenticated) return;
    
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityTimeRef.current;
    console.log(`[세션] 마지막 활동 후 경과 시간: ${Math.floor(timeSinceLastActivity / 1000)}초`);
    
    // 세션 타임아웃 시간이 지났다면 상태 관리자에서 처리하도록 위임
    // 상태 관리자에서 플랫폼 정보를 저장하고 리다이렉트 처리
    if (timeSinceLastActivity >= SESSION_TIMEOUT) {
      console.log('[세션] 세션 타임아웃: 30분 동안 활동이 없어 로그아웃합니다.');
      // 세션 타임아웃 처리는 상태 관리자의 checkSessionTimeout 함수에서 처리
      useAuthStore.getState().checkSessionTimeout();
    }
  };
  
  useEffect(() => {
    // 인증된 사용자만 세션 타임아웃 관리
    if (!isAuthenticated) return;

    // 페이지 로드 시 세션 스토리지에서 마지막 활동 시간 확인
    const authData = JSON.parse(sessionStorage.getItem('auth-storage') || '{}');
    const storedLastActivity = authData?.state?.lastActivity;
    
    if (storedLastActivity) {
      const now = Date.now();
      const timeSinceLastActivity = now - storedLastActivity;
      console.log(`[세션] 페이지 로드 - 마지막 활동 후 경과 시간: ${Math.floor(timeSinceLastActivity / 1000)}초`);
      
      // 세션 타임아웃 시간이 지났다면 상태 관리자에서 처리하도록 위임
      if (timeSinceLastActivity >= SESSION_TIMEOUT) {
        console.log('[세션] 페이지 로드 - 세션 타임아웃: 30분 동안 활동이 없어 로그아웃합니다.');
        // 세션 타임아웃 처리는 상태 관리자의 checkSessionTimeout 함수에서 처리
        useAuthStore.getState().checkSessionTimeout();
        return;
      }
      
      // 유효한 세션이면 마지막 활동 시간 업데이트
      lastActivityTimeRef.current = storedLastActivity;
    }

    // 초기 활동 기록 - 한 번만 실행
    if (!initialActivityRecordedRef.current) {
      console.log('[세션] 초기 세션 활동 기록');
      updateActivity();
      lastActivityTimeRef.current = Date.now();
      initialActivityRecordedRef.current = true;
    }
    
    // 사용자 활동을 감지할 이벤트 리스너 등록
    // mousemove는 제외하고 더 중요한 상호작용 이벤트만 등록
    const events = ['mousedown', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      window.addEventListener(event, debouncedUpdateActivity);
    });
    
    // 주기적으로 세션 타임아웃 체크 (1분마다)
    sessionCheckTimerRef.current = window.setInterval(checkSessionTimeout, 60 * 1000);

    // 컴포넌트 언마운트 시 정리
    return () => {
      // 이벤트 리스너 제거
      events.forEach(event => {
        window.removeEventListener(event, debouncedUpdateActivity);
      });
      
      // 타이머 취소
      if (debounceTimerRef.current !== null) {
        window.clearTimeout(debounceTimerRef.current);
      }
      
      if (sessionCheckTimerRef.current !== null) {
        window.clearInterval(sessionCheckTimerRef.current);
      }
    };
  }, [isAuthenticated, updateActivity]);

  // 이 컴포넌트는 UI를 렌더링하지 않음
  return null;
};

export default SessionTimeoutManager;
